import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from './Layout';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function Clinics() {
  const [targets, setTargets] = useState([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [addressesFor, setAddressesFor] = useState(null);
  const [doctorVerified, setDoctorVerified] = useState(true);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ clinic_name: '', city: '', website: '' });
  const [suggestError, setSuggestError] = useState('');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestDone, setSuggestDone] = useState(false);

  useEffect(() => {
    const h = { Authorization: 'Bearer ' + localStorage.getItem('token') };
    axios.get(`${API}/clinics/targets`, { headers: h })
      .then(r => { setTargets(r.data.targets || []); setDoctorVerified(r.data.doctor_verified !== false); setLoaded(true); })
      .catch(() => { localStorage.clear(); window.location.href = '/login'; });
  }, []);

  const submitSuggest = async () => {
    if (!suggestForm.clinic_name.trim() || !suggestForm.city.trim()) { setSuggestError('Укажите название клиники и город'); return; }
    setSuggestLoading(true); setSuggestError('');
    try {
      const h = { Authorization: 'Bearer ' + localStorage.getItem('token') };
      await axios.post(`${API}/auth/me/suggest-clinic`, suggestForm, { headers: h });
      setSuggestDone(true);
    } catch (e) { setSuggestError(e.response?.data?.detail || 'Не удалось отправить'); }
    setSuggestLoading(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return targets.filter(t => {
      if (type && t.target_type !== type) return false;
      if (q && !t.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [targets, search, type]);

  return (
    <Layout>
      <div style={s.topRow}>
        <h1 style={s.h1}>Клиники</h1>
        <button style={s.suggestBtn} onClick={() => { setSuggestOpen(true); setSuggestDone(false); setSuggestError(''); setSuggestForm({ clinic_name: '', city: '', website: '' }); }}>
          Предложить клинику
        </button>
      </div>
      {!doctorVerified && (
        <div style={s.bannerWarn}>
          Условия клиник (%, квота, срок ожидания) скрыты до подтверждения выписки ЕГИСЗ.
        </div>
      )}
      <div style={s.filterBar}>
        <input style={s.search} placeholder="Поиск клиник…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.select} value={type} onChange={e => setType(e.target.value)}>
          <option value="">Все цели</option>
          <option value="clinic">Клиники</option>
          <option value="network">Сети</option>
        </select>
      </div>

      {!loaded ? <div style={s.muted}>Загрузка…</div> : filtered.length === 0 ? (
        <div style={s.empty}>Доступных клиник не найдено</div>
      ) : (
        <div style={s.grid}>
          {filtered.map(t => (
            <div key={`${t.target_type}:${t.id}`} style={s.card}>
              <div style={s.cardHead}>
                <div style={s.cardName}>{t.name}</div>
                <span style={t.target_type === 'network' ? s.tagNet : s.tagClinic}>
                  {t.target_type === 'network' ? `сеть · ${t.member_count} учр.` : 'клиника'}
                </span>
              </div>
              {t.conditions_hidden ? (
                <div style={s.cond}>
                  <div style={s.condRow}><span style={s.condLabel}>Первичная</span><b>***</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Повторная</span><b>***</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Квота</span><b>***</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Таймер</span><b>***</b></div>
                </div>
              ) : t.commission_primary_pct != null ? (
                <div style={s.cond}>
                  <div style={s.condRow}><span style={s.condLabel}>Первичная</span><b>{t.commission_primary_pct}%</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Повторная</span><b>{t.commission_repeat_pct}%</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Квота</span><b>{t.monthly_quota}/мес</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Таймер</span><b>{t.timeout_days} дн.</b></div>
                </div>
              ) : <div style={s.noCond}>Условия ещё не заданы</div>}

              <div style={s.stats}>
                <div style={s.condRow}><span style={s.condLabel}>Дошедшие (всё время)</span><b>{t.reached_count}</b></div>
                <div style={s.condRow}><span style={s.condLabel}>Квота (текущий месяц)</span><b>{t.quota_fact}/{t.monthly_quota ?? '—'}</b></div>
              </div>

              {/* N-118: условия параллельной программы «Операции» (N-115) — своя секция ПОСЛЕ
                  квоты/дошедших, т.к. те относятся только к стандартной модели направлений;
                  у «Операций» нет ни квоты, ни повторных визитов. Показываем только если у
                  цели вообще настроена программа (иначе не засоряем карточку). */}
              {t.conditions_hidden ? (
                <div style={s.condOp}>
                  <div style={s.condOpTitle}>Условия «Операций»</div>
                  <div style={s.condRow}><span style={s.condLabel}>Комиссия</span><b>***</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Окно</span><b>*** дн.</b></div>
                </div>
              ) : t.operation_commission_pct != null && (
                <div style={s.condOp}>
                  <div style={s.condOpTitle}>Условия «Операций»</div>
                  <div style={s.condRow}><span style={s.condLabel}>Комиссия</span><b>{t.operation_commission_pct}%</b></div>
                  <div style={s.condRow}><span style={s.condLabel}>Окно</span><b>{t.operation_timeout_days} дн.</b></div>
                </div>
              )}

              {t.target_type === 'clinic' && (t.address || t.phone) && (
                <div style={s.contact}>
                  {t.address && <div style={s.contactRow}>{t.address}</div>}
                  {t.phone && <div style={s.contactRow}>{t.phone}</div>}
                </div>
              )}

              {t.target_type === 'network' && t.members && t.members.length > 0 && (
                <button style={s.addressesBtn} onClick={() => setAddressesFor(t)}>
                  Показать адреса ({t.members.length})
                </button>
              )}

              <a style={s.servicesLink} href="https://referomed.ru" target="_blank" rel="noreferrer">
                Все услуги →
              </a>
            </div>
          ))}
        </div>
      )}

      {addressesFor && (
        <div style={s.overlay} onClick={() => setAddressesFor(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{addressesFor.name} — адреса учреждений</h3>
            {addressesFor.phone && <div style={s.modalPhone}>Телефон УК: {addressesFor.phone}</div>}
            <div style={s.modalList}>
              {addressesFor.members.map((m, i) => (
                <div key={i} style={s.modalRow}>
                  <div style={s.modalRowName}>{m.name}</div>
                  <div style={s.modalRowAddr}>{m.address || 'адрес не указан'}</div>
                  {m.phone && <div style={s.modalRowAddr}>{m.phone}</div>}
                </div>
              ))}
            </div>
            <button style={s.modalClose} onClick={() => setAddressesFor(null)}>Закрыть</button>
          </div>
        </div>
      )}

      {suggestOpen && (
        <div style={s.overlay} onClick={() => setSuggestOpen(false)}>
          <div style={s.modalSm} onClick={e => e.stopPropagation()}>
            {suggestDone ? (
              <>
                <h3 style={s.modalTitle}>Спасибо!</h3>
                <p style={s.suggestDoneText}>Мы свяжемся с клиникой и предложим ей подключиться к Refero.</p>
                <button style={s.modalClose} onClick={() => setSuggestOpen(false)}>Закрыть</button>
              </>
            ) : (
              <>
                <h3 style={s.modalTitle}>Предложить клинику</h3>
                <p style={s.suggestIntro}>Уже направляете пациентов куда-то не через Refero? Расскажите — мы свяжемся с клиникой и предложим подключиться.</p>
                <input style={s.input} placeholder="Название клиники *" value={suggestForm.clinic_name} onChange={e => setSuggestForm({ ...suggestForm, clinic_name: e.target.value })} />
                <input style={s.input} placeholder="Город *" value={suggestForm.city} onChange={e => setSuggestForm({ ...suggestForm, city: e.target.value })} />
                <input style={s.input} placeholder="Сайт (необязательно)" value={suggestForm.website} onChange={e => setSuggestForm({ ...suggestForm, website: e.target.value })} />
                {suggestError && <div style={s.error}>{suggestError}</div>}
                <div style={s.modalBtns}>
                  <button style={s.modalGhost} onClick={() => setSuggestOpen(false)}>Отмена</button>
                  <button style={s.suggestBtn} onClick={submitSuggest} disabled={suggestLoading}>{suggestLoading ? 'Отправка…' : 'Отправить'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

const s = {
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h1: { color: '#1B2B4B', fontSize: 28, fontWeight: 800, margin: 0 },
  muted: { color: '#64748b', fontSize: 14 },
  bannerWarn: { background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 12, padding: '14px 18px', fontSize: 13, marginBottom: 16, lineHeight: 1.5 },
  suggestBtn: { background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  modalSm: { background: '#fff', borderRadius: 20, padding: 28, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 20px 60px rgba(15,23,42,0.3)' },
  suggestIntro: { fontSize: 13, color: '#64748b', margin: '0 0 4px', lineHeight: 1.5 },
  suggestDoneText: { fontSize: 14, color: '#475569', lineHeight: 1.5, margin: '0 0 4px' },
  input: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '11px 14px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  modalBtns: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 4 },
  modalGhost: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 16px', fontSize: 14, cursor: 'pointer' },
  filterBar: { display: 'flex', gap: 10, marginBottom: 20 },
  search: { flex: 1, maxWidth: 360, background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none' },
  select: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 },
  card: { background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  cardHead: { marginBottom: 14 },
  cardName: { fontSize: 16, fontWeight: 700, color: '#1B2B4B', marginBottom: 8 },
  tagNet: { fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#f3e8ff', padding: '3px 10px', borderRadius: 20 },
  tagClinic: { fontSize: 11, fontWeight: 600, color: '#149e90', background: '#e9faf7', padding: '3px 10px', borderRadius: 20 },
  cond: { display: 'flex', flexDirection: 'column', gap: 8 },
  condRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#1B2B4B' },
  condLabel: { color: '#64748b' },
  noCond: { fontSize: 13, color: '#94a3b8' },
  stats: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #eef1f5' },
  condOp: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid #eef1f5' },
  condOpTitle: { fontSize: 12, fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '40px 0', fontSize: 14 },
  contact: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #eef1f5', display: 'flex', flexDirection: 'column', gap: 4 },
  contactRow: { fontSize: 13, color: '#475569', lineHeight: 1.4 },
  addressesBtn: { marginTop: 12, width: '100%', background: '#f1f5f9', color: '#1B2B4B', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  servicesLink: { display: 'block', textAlign: 'center', marginTop: 10, fontSize: 13, fontWeight: 600, color: '#149e90', textDecoration: 'none' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 20, padding: 28, width: 480, maxWidth: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(15,23,42,0.3)' },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#1B2B4B', margin: '0 0 6px' },
  modalPhone: { fontSize: 13, color: '#64748b', marginBottom: 14 },
  modalList: { display: 'flex', flexDirection: 'column', gap: 10 },
  modalRow: { padding: '10px 0', borderTop: '1px solid #eef1f5' },
  modalRowName: { fontSize: 14, fontWeight: 600, color: '#1B2B4B' },
  modalRowAddr: { fontSize: 13, color: '#64748b' },
  modalClose: { marginTop: 18, width: '100%', background: '#1B2B4B', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
